import { useFrame } from "@react-three/fiber";
import { useMemo, useRef } from "react";
import * as THREE from "three";
import { useObserverStore } from "../../stores/observerStore";
import type { StarSystem } from "../../types/celestial";

const BASE_SCALE = 10;
const PULSE_DURATION = 2000; // 2 seconds
const MAX_RADIUS = 30;

const BUY_COLOR = new THREE.Color("#73daca");
const SELL_COLOR = new THREE.Color("#e0af68");

/** Expanding ring pulses at trade locations */
export function TradePulses(): React.JSX.Element | null {
	const recentTrades = useObserverStore((s) => s.recentTrades);
	const systems = useObserverStore((s) => s.systems);
	const pruneOldTrades = useObserverStore((s) => s.pruneOldTrades);

	// Build planet→system lookup
	const systemLookup = useMemo(() => {
		const map = new Map<string, StarSystem>();
		for (const sys of systems) {
			map.set(sys.id, sys);
			for (const p of sys.planets) {
				map.set(p.id, sys);
			}
		}
		return map;
	}, [systems]);

	// Prune expired trades each frame
	useFrame(() => {
		const now = Date.now();
		const hasExpired = recentTrades.some((t) => now >= t.fadeOut);
		if (hasExpired) pruneOldTrades();
	});

	if (recentTrades.length === 0) return null;

	return (
		<group renderOrder={4}>
			{recentTrades.map((trade) => {
				const sys = systemLookup.get(trade.planetId);
				if (!sys) return null;
				return (
					<TradePulse
						key={trade.id}
						x={sys.x * BASE_SCALE}
						y={-(sys.y * BASE_SCALE)}
						color={trade.action === "buy" ? BUY_COLOR : SELL_COLOR}
						createdAt={trade.timestamp}
					/>
				);
			})}
		</group>
	);
}

function TradePulse({
	x,
	y,
	color,
	createdAt,
}: {
	x: number;
	y: number;
	color: THREE.Color;
	createdAt: number;
}): React.JSX.Element {
	const ringRef = useRef<THREE.Mesh>(null);

	useFrame(() => {
		if (!ringRef.current) return;
		const elapsed = Date.now() - createdAt;
		const progress = Math.min(1, elapsed / PULSE_DURATION);
		const scale = progress * MAX_RADIUS;
		ringRef.current.scale.setScalar(scale || 0.01);
		(ringRef.current.material as THREE.MeshBasicMaterial).opacity = (1 - progress) * 0.6;
	});

	return (
		<mesh ref={ringRef} position={[x, y, 0.5]}>
			<ringGeometry args={[0.8, 1, 32]} />
			<meshBasicMaterial
				color={color}
				transparent
				opacity={0.6}
				side={THREE.DoubleSide}
				depthWrite={false}
			/>
		</mesh>
	);
}
